// QR Kiosk v2 — KV2-2 report detail actions (status- AND permission-gated).
// Open (Write): solve hero + PDF + Edit; Open (Admin): quiet delete + kiosk confirm
// sheet; Open (Viewer): PDF only. Solved (any role): PDF only. Delete flow is v1
// verbatim (KV2-2-k) plus a local `deleting` double-tap guard. v1's MUI useConfirmation
// is BANNED — the delete confirm uses the shared KioskSheet (Round-6: portaled + keyboard-safe).
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "../../../shared/hooks/sharedHooks";
import useTranslation from "../../../shared/hooks/useTranslation";
import { deleteServiceReport, getServiceReports, selectServiceReport } from "../../publicSlice";
import { ServiceReport, ServiceReportStatusCode } from "../../models/serviceReportsModels";
import publicServiceReportsApi from "../../api/publicServiceReportsApi";
import { Glyph, GD } from "../shared/glyphs";
import { usePressed } from "../shared/usePressed";
import { ErrorCallout } from "../shared/ErrorCallout";
import { KioskSheet } from "../shared/KioskSheet";

export interface ReportDetailActionsProps {
  report: ServiceReport;
  canWrite: boolean;
  canAdmin: boolean;
}

const neutralBtn: React.CSSProperties = {
  borderRadius: 11,
  border: "1px solid var(--border)",
  background: "var(--card)",
  color: "var(--ink-2)",
  fontFamily: "inherit",
  fontWeight: 600,
  cursor: "pointer",
};

export function ReportDetailActions({ report, canWrite, canAdmin }: ReportDetailActionsProps) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { translate } = useTranslation("public");
  const { translate: tCommon } = useTranslation("common");
  const solveHero = usePressed();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(false); // KV2-5 BUG-2

  const open = report.statusCode === ServiceReportStatusCode.inProgress;

  // Close the confirm sheet AND clear any surfaced delete error.
  const closeConfirm = () => {
    setConfirmOpen(false);
    setDeleteError(false);
  };

  const handlePdf = () => publicServiceReportsApi.getServiceReportPdfLink(report.number, `service-report-${report.number}.pdf`);
  const handleEdit = () => {
    dispatch(selectServiceReport(report));
    navigate(`/public-v2/service-reports/edit/${report.number}`);
  };
  const handleSolve = () => {
    dispatch(selectServiceReport(report));
    navigate(`/public-v2/service-reports/solving/${report.number}`);
  };
  // BUG-2 fix (KV2-5): match `rejected` — offline the report still exists, so surface
  // the error in the sheet instead of silently bouncing to the dashboard. On success
  // the v1 flow is verbatim (refresh + navigate, keyed on report.machineNumber, KV2-2-d);
  // a rejected follow-up refresh still navigates (the dashboard refetches on mount).
  const handleDelete = async () => {
    setDeleting(true);
    setDeleteError(false);
    const res = await dispatch(deleteServiceReport(report.number));
    if (deleteServiceReport.rejected.match(res)) {
      setDeleting(false);
      setDeleteError(true);
      return;
    }
    await dispatch(getServiceReports({ machineNumber: report.machineNumber, query: { statusCodes: ["InProgress", "Solved"] } }));
    navigate(`/public-v2/machines/${report.machineNumber}`);
  };

  return (
    <div
      className="kv2-enter"
      style={{ display: "flex", flexDirection: "column", margin: "12px 14px 0", flexShrink: 0, animationDelay: "0.05s" }}
    >
      {open ? (
        <>
          {canWrite && (
            <button
              type="button"
              {...solveHero.pressHandlers}
              onClick={handleSolve}
              style={{
                height: 56,
                borderRadius: 12,
                border: "1px solid color-mix(in oklab, var(--green) 55%, var(--border))",
                background: "color-mix(in oklab, var(--green) 82%, var(--ink))",
                color: "var(--tier-accent-fg)",
                fontFamily: "inherit",
                fontSize: 16,
                fontWeight: 700,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 9,
                cursor: "pointer",
                ...solveHero.pressStyle,
              }}
            >
              <Glyph d={GD.check} size={20} />
              {translate("markAsSolved")}
            </button>
          )}

          <div style={{ display: "flex", gap: 9, marginTop: canWrite ? 9 : 0 }}>
            <button type="button" onClick={handlePdf} style={{ ...neutralBtn, flex: 1, height: 50, fontSize: 14.5 }}>
              {translate("downloadPdf")}
            </button>
            {canWrite && (
              <button type="button" onClick={handleEdit} style={{ ...neutralBtn, flex: 1, height: 50, fontSize: 14.5 }}>
                {tCommon("buttons.edit")}
              </button>
            )}
          </div>

          {canAdmin && (
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              style={{
                height: 44,
                marginTop: 9,
                width: "100%",
                border: "none",
                background: "transparent",
                color: "color-mix(in oklab, var(--red) 75%, var(--ink))",
                fontFamily: "inherit",
                fontSize: 13.5,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {translate("deleteServiceReport")}
            </button>
          )}
        </>
      ) : (
        <button type="button" onClick={handlePdf} style={{ ...neutralBtn, width: "100%", height: 52, fontSize: 15 }}>
          {translate("downloadPdf")}
        </button>
      )}

      {/* Delete confirm — shared KioskSheet (portaled + keyboard-safe). The in-flight
          `deleting` guard blocks dismiss, same as the former raw-radix onOpenChange. */}
      <KioskSheet
        open={confirmOpen}
        onRequestClose={() => { if (!deleting) closeConfirm(); }}
        title={translate("deleteServiceReport")}
      >
        <div style={{ fontSize: 13, color: "var(--muted)", marginTop: 6, lineHeight: 1.5 }}>
          {translate("deleteConfirm", { number: report.number })}
        </div>
        {deleteError && <ErrorCallout title={translate("submitFailed")} />}
        <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
          <button
            type="button"
            disabled={deleting}
            onClick={closeConfirm}
            style={{
              flex: 1,
              height: 48,
              borderRadius: 11,
              border: "1px solid var(--border)",
              background: "var(--bg)",
              color: "var(--ink-2)",
              fontFamily: "inherit",
              fontSize: 14.5,
              fontWeight: 600,
              cursor: "pointer",
              opacity: deleting ? 0.6 : 1,
            }}
          >
            {tCommon("common.no")}
          </button>
          <button
            type="button"
            disabled={deleting}
            onClick={handleDelete}
            style={{
              flex: 1,
              height: 48,
              borderRadius: 11,
              border: "1px solid color-mix(in oklab, var(--red) 55%, var(--border))",
              background: "color-mix(in oklab, var(--red) 82%, var(--ink))",
              color: "var(--tier-accent-fg)",
              fontFamily: "inherit",
              fontSize: 14.5,
              fontWeight: 700,
              cursor: "pointer",
              opacity: deleting ? 0.6 : 1,
            }}
          >
            {translate("yesDelete")}
          </button>
        </div>
      </KioskSheet>
    </div>
  );
}

export default ReportDetailActions;
